import request from "../shared/http";

function apiErrorMessage(err, fallback = "Request failed") {
  const data = err?.response?.data;
  if (typeof data?.message === "string" && data.message) return data.message;
  if (typeof data?.error === "string" && data.error) return data.error;
  if (typeof data === "string" && data) return data;
  if (err?.message) return err.message;
  return fallback;
}

class ApiService {
  static async getHealth() {
    const { data } = await request.get("/health");
    return data;
  }

  static async googleClientId() {
    const { data } = await request.get("/auth/google/client-id");
    return data;
  }

  static async facebookAppId() {
    const { data } = await request.get("/auth/facebook/app-id");
    return data;
  }

  static async signInWithGoogle(credential) {
    const { data } = await request.post("/auth/google", { credential });
    return data;
  }

  static async signInWithFacebook(accessToken) {
    const { data } = await request.post("/auth/facebook", { access_token: accessToken });
    return data;
  }

  static async signin(username, password, type = "user") {
    const { data } = await request.post("/signin", { username, password, type });
    return data;
  }

  static async sendLoginOtp(loginId) {
    try {
      const { data } = await request.post("/auth/login-verification", {
        login_id: String(loginId || "").trim(),
      });
      return data?.id;
    } catch (err) {
      throw new Error(apiErrorMessage(err, "Could not send OTP"));
    }
  }

  static async signInWithOtp({ loginId, verificationId, verificationCode }) {
    try {
      const { data } = await request.post("/signin/otp", {
        login_id: String(loginId || "").trim(),
        verification_id: verificationId,
        verification_code: verificationCode,
        type: "user",
      });
      return data;
    } catch (err) {
      throw new Error(apiErrorMessage(err, "OTP sign-in failed"));
    }
  }

  static async signupVerificationCode(mobile, firstName) {
    try {
      const payload = {};
      if (mobile) payload.login_mobile = String(mobile).trim();
      if (firstName) payload.first_name = firstName;
      const { data } = await request.post("/auth/userverification", payload);
      return data;
    } catch (err) {
      throw new Error(apiErrorMessage(err, "Could not send verification code"));
    }
  }

  static async signup(payload) {
    try {
      const body = {
        first_name: payload.firstName,
        last_name: payload.lastName || "",
        mobile: payload.mobile,
        email: payload.email || undefined,
        password: payload.password,
        mobile_verification_id: payload.mobileVerificationId,
        mobile_verification_code: payload.mobileVerificationCode,
        type: "user",
      };
      const { data } = await request.post("/signup", body);
      return data;
    } catch (err) {
      throw new Error(apiErrorMessage(err, "Sign up failed"));
    }
  }

  static async requestPasswordReset(loginId) {
    try {
      const { data } = await request.post("/auth/password-reset/request", {
        login_id: String(loginId || "").trim(),
      });
      return data?.verification_id ?? data?.id;
    } catch (err) {
      throw new Error(apiErrorMessage(err, "Could not send reset code"));
    }
  }

  static async resetPassword({
    verificationId, verificationCode, loginId, password,
  }) {
    try {
      await request.post("/auth/reset-password", {
        verification_id: verificationId,
        verification_code: verificationCode,
        login_id: loginId,
        password,
      });
      return true;
    } catch (err) {
      throw new Error(apiErrorMessage(err, "Password reset failed"));
    }
  }

  static async signout() {
    const { data } = await request.post("/signout");
    return data;
  }

  static async getAccountMe() {
    const { data } = await request.get("/accounts/@me");
    return data;
  }
}

export default ApiService;
